import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { e2eAgentApi } from "../api/e2eAgentClient";
import type { components } from "../api/e2eAgentSchema";

export type ApiToken = components["schemas"]["ApiTokenOut"];
export type CreatedApiToken = components["schemas"]["CreatedApiTokenOut"];

const queryKey = ["e2e-agent", "api-tokens"] as const;

function ensure<Data>(data: Data | undefined, error: unknown): Data {
  if (error !== undefined) {
    const detail =
      error && typeof error === "object" && "detail" in error
        ? JSON.stringify(error.detail)
        : JSON.stringify(error);
    throw new Error(`Token request failed: ${detail}`);
  }
  if (data === undefined) throw new Error("Token response was empty.");
  return data;
}

export function useApiTokens() {
  return useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await e2eAgentApi.GET("/v1/api-tokens", {
        params: { query: { scope: "own" } },
      });
      return ensure(data, error);
    },
  });
}

export function useCreateApiToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: {
      name: string;
      expires_in_days: number | null;
    }) => {
      const { data, error } = await e2eAgentApi.POST("/v1/api-tokens", {
        body: request,
      });
      return ensure(data, error);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useRevokeApiToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tokenId: string) => {
      const { error } = await e2eAgentApi.DELETE("/v1/api-tokens/{token_id}", {
        params: { path: { token_id: tokenId } },
      });
      if (error !== undefined) ensure(undefined, error);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  });
}
